import React, {
    forwardRef,
    useMemo,
    useRef,
    useEffect,
    useState,
    useImperativeHandle,
} from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Pagination, Select, Checkbox, Alert } from 'components/ui'
import TableRowSkeleton from './loaders/TableRowSkeleton'
import Loading from './Loading'
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table'
import Table from 'components/ui/Table'
import RecordNotFound from 'components/RecordNotFound'

const { Tr, Th, Td, THead, TBody, Sorter } = Table

const IndeterminateCheckbox = (props) => {
    const {
        indeterminate,
        onChange,
        onCheckBoxChange,
        onIndeterminateCheckBoxChange,
        ...rest
    } = props

    const ref = useRef(null)

    useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, indeterminate])

    const handleChange = (e) => {
        onChange(e)
        onCheckBoxChange?.(e)
        onIndeterminateCheckBoxChange?.(e)
    }

    return (
        <Checkbox
            className="mb-0"
            ref={ref}
            onChange={(_, e) => handleChange(e)}
            {...rest}
        />
    )
}

const DataTable = forwardRef((props, ref) => {
    const {
        skeletonAvatarColumns,
        columns: columnsProp,
        data,
        loading,
        onCheckBoxChange,
        onIndeterminateCheckBoxChange,
        onPaginationChange,
        onSelectChange,
        onSort,
        pageSizes,
        selectable,
        skeletonAvatarProps,
        pagingData,
        isPageSelector = true,
        noRecordCssClass = '',
        // Optional: when set, every row becomes clickable and forwards
        // its `row.original` to the callback. Cursor turns pointer when
        // present; undefined when not (no behaviour change for existing
        // consumers).
        onRowClick,
    } = props

    const { pageSize, pageIndex, total } = pagingData

    const [sorting, setSorting] = useState(null)
    const [columnSizing, setColumnSizing] = useState({})
    const containerRef = useRef(null)
    const [containerWidth, setContainerWidth] = useState(0)

    const pageSizeOption = useMemo(
        () =>
            pageSizes.map((number) => ({
                value: number,
                label: `${number} / page`,
            })),
        [pageSizes]
    )

    const handleCheckBoxChange = (checked, row) => {
        if (!loading) {
            onCheckBoxChange?.(checked, row)
        }
    }

    const handleIndeterminateCheckBoxChange = (checked, rows) => {
        if (!loading) {
            onIndeterminateCheckBoxChange?.(checked, rows)
        }
    }

    const handlePaginationChange = (page) => {
        if (!loading) {
            onPaginationChange?.(page)
        }
    }

    const handleSelectChange = (value) => {
        if (!loading) {
            onSelectChange?.(Number(value))
        }
    }

    useEffect(() => {
        if (Array.isArray(sorting)) {
            const sortOrder =
                sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : ''
            const id = sorting.length > 0 ? sorting[0].id : ''
            onSort?.({ order: sortOrder, key: id })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sorting])

    const hasOldColumnMetaKey = columnsProp.some(
        (col) => col.Header || col.accessor || col.Cell
    )

    const finalColumns = useMemo(() => {
        const columns = columnsProp

        if (selectable) {
            return [
                {
                    id: 'select',
                    header: ({ table }) => (
                        <IndeterminateCheckbox
                            checked={table.getIsAllRowsSelected()}
                            indeterminate={table.getIsSomeRowsSelected()}
                            onChange={table.getToggleAllRowsSelectedHandler()}
                            onIndeterminateCheckBoxChange={(e) => {
                                handleIndeterminateCheckBoxChange(
                                    e.target.checked,
                                    table.getRowModel().rows
                                )
                            }}
                        />
                    ),
                    cell: ({ row }) => (
                        <IndeterminateCheckbox
                            checked={row.getIsSelected()}
                            disabled={!row.getCanSelect()}
                            indeterminate={row.getIsSomeSelected()}
                            onChange={row.getToggleSelectedHandler()}
                            onCheckBoxChange={(e) =>
                                handleCheckBoxChange(
                                    e.target.checked,
                                    row.original
                                )
                            }
                        />
                    ),
                },
                ...columns,
            ]
        }
        return columns
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsProp, selectable])

    // Check if any column has resizing enabled (must be before any early returns)
    const hasResizableColumns = useMemo(() => {
        return finalColumns.some((col) => col.enableResizing)
    }, [finalColumns])

    const table = useReactTable({
        data,
        columns: hasOldColumnMetaKey ? [] : finalColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        manualPagination: true,
        manualSorting: true,
        onSortingChange: setSorting,
        onColumnSizingChange: setColumnSizing,
        columnResizeMode: 'onChange',
        defaultColumn: {
            enableResizing: false,
        },
        state: {
            sorting,
            columnSizing,
        },
    })

    const resetSorting = () => {
        table.resetSorting()
    }

    const resetSelected = () => {
        table.toggleAllRowsSelected(false)
    }

    useImperativeHandle(ref, () => ({
        resetSorting,
        resetSelected,
    }))

    // Measure container width to ensure table is at least 100% wide
    useEffect(() => {
        if (!hasResizableColumns || !containerRef.current) return

        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth)
            }
        }

        updateWidth()
        const resizeObserver = new ResizeObserver(updateWidth)
        resizeObserver.observe(containerRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [hasResizableColumns, data])

    // Calculate total table width from column sizes when resizable
    // This allows the table to expand horizontally instead of collapsing columns
    // Must be before any early returns to follow rules of hooks
    const tableWidth = useMemo(() => {
        if (!hasResizableColumns) return undefined

        const headerGroup = table.getHeaderGroups()[0]
        if (!headerGroup) return '100%'

        let totalWidth = 0
        headerGroup.headers.forEach((header) => {
            // Use the actual rendered size from the table state
            const size = header.getSize()
            if (size && size > 0) {
                totalWidth += size
            } else {
                // Fallback to column definition size
                const colDef = header.column.columnDef
                const defaultSize =
                    colDef.size || (header.column.getCanResize() ? 250 : 200)
                totalWidth += defaultSize
            }
        })

        // Add a buffer to account for borders, padding, and prevent cutoff
        // This ensures the last column is fully visible when scrolling
        const buffer = 4
        const calculatedWidth = totalWidth + buffer

        // Ensure table is at least 100% of container width, but can expand beyond that
        const minWidth = containerWidth > 0 ? containerWidth : undefined
        const finalWidth =
            minWidth && calculatedWidth < minWidth ? minWidth : calculatedWidth

        return finalWidth > 0 ? `${finalWidth}px` : '100%'
    }, [
        hasResizableColumns,
        table,
        containerWidth,
    ])

    if (hasOldColumnMetaKey) {
        const message =
            'You are using old react-table v7 column config, please use v8 column config instead, refer to our demo or https://tanstack.com/table/v8'

        if (process.env.NODE_ENV === 'development') {
            console.warn(message)
        }

        return <Alert>{message}</Alert>
    }

    return (
        <Loading loading={loading && data.length !== 0} type="cover">
            <div
                ref={containerRef}
                className={hasResizableColumns ? 'overflow-x-auto pr-2' : ''}
            >
                <Table
                    className="bg-white shadow-md"
                    oveerflow={!hasResizableColumns}
                    style={
                        hasResizableColumns
                            ? {
                                  tableLayout: 'fixed',
                                  ...(data.length > 0 && { width: tableWidth || '100%' }),
                              }
                            : {}
                    }
                >
                    <THead className="bg-white border-b-1 border-gray-300">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id} className="bg-white">
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <Th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className={classNames(
                                                '!text-gray-800 relative group font-bold text-sm uppercase tracking-wider py-4 px-6 !bg-[#f9fafb]',
                                                header.column.columnDef.meta
                                                    ?.headerClassName
                                            )}
                                            style={{
                                                // Set width for resizable columns
                                                ...(header.column.getCanResize() && {
                                                    width: header.getSize(),
                                                    ...(header.column.columnDef
                                                        .minSize && {
                                                        minWidth:
                                                            header.column
                                                                .columnDef
                                                                .minSize,
                                                    }),
                                                    ...(header.column.columnDef
                                                        .maxSize && {
                                                        maxWidth:
                                                            header.column
                                                                .columnDef
                                                                .maxSize,
                                                    }),
                                                }),
                                                // Set width for non-resizable columns to prevent collapse
                                                ...(hasResizableColumns &&
                                                    !header.column.getCanResize() && {
                                                        width:
                                                            header.column
                                                                .columnDef
                                                                .size || 200,
                                                        minWidth:
                                                            header.column
                                                                .columnDef
                                                                .size || 200,
                                                    }),
                                            }}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    className={classNames(
                                                        header.column.getCanSort() &&
                                                            'cursor-pointer select-none point text-black',
                                                        loading &&
                                                            'pointer-events-none text-black'
                                                    )}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef
                                                            .header,
                                                        header.getContext()
                                                    )}
                                                    {header.column.getCanSort() && (
                                                        <Sorter
                                                            sort={header.column.getIsSorted()}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            {header.column.getCanResize() && (
                                                <div
                                                    onMouseDown={header.getResizeHandler()}
                                                    onTouchStart={header.getResizeHandler()}
                                                    className={classNames(
                                                        'absolute right-0 top-0 h-full w-1 bg-gray-400 cursor-col-resize select-none touch-none opacity-30 group-hover:opacity-100 transition-opacity z-20',
                                                        header.column.getIsResizing() &&
                                                            'bg-blue-500 opacity-100'
                                                    )}
                                                    style={{
                                                        transform:
                                                            header.column.getIsResizing()
                                                                ? `translateX(${
                                                                      table.getState()
                                                                          .columnSizingInfo
                                                                          .deltaOffset
                                                                  }px)`
                                                                : undefined,
                                                    }}
                                                />
                                            )}
                                        </Th>
                                    )
                                })}
                            </Tr>
                        ))}
                    </THead>
                    {loading && data.length === 0 ? (
                        <TableRowSkeleton
                            columns={finalColumns.length}
                            rows={pagingData.pageSize}
                            avatarInColumns={skeletonAvatarColumns}
                            avatarProps={skeletonAvatarProps}
                        />
                    ) : (
                        <TBody>
                            {table.getRowModel().rows?.length === 0 &&
                            !loading ? (
                                <Tr key={'record-not-found'}>
                                    <Td colSpan="100">
                                        <RecordNotFound
                                            cssClass={noRecordCssClass}
                                        />
                                    </Td>
                                </Tr>
                            ) : (
                                table
                                    .getRowModel()
                                    .rows.slice()
                                    .map((row) => (
                                        <Tr
                                            key={row.id}
                                            onClick={
                                                onRowClick
                                                    ? () =>
                                                          onRowClick(
                                                              row.original
                                                          )
                                                    : undefined
                                            }
                                            style={
                                                onRowClick
                                                    ? { cursor: 'pointer' }
                                                    : undefined
                                            }
                                        >
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => (
                                                    <Td
                                                        key={cell.id}
                                                        style={{
                                                            // Set width for resizable columns
                                                            ...(cell.column.getCanResize() && {
                                                                width: cell.column.getSize(),
                                                                ...(cell.column
                                                                    .columnDef
                                                                    .minSize && {
                                                                    minWidth:
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .minSize,
                                                                }),
                                                                ...(cell.column
                                                                    .columnDef
                                                                    .maxSize && {
                                                                    maxWidth:
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .maxSize,
                                                                }),
                                                            }),
                                                            // Set width for non-resizable columns to prevent collapse
                                                            ...(hasResizableColumns &&
                                                                !cell.column.getCanResize() && {
                                                                    width:
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .size ||
                                                                        200,
                                                                    minWidth:
                                                                        cell
                                                                            .column
                                                                            .columnDef
                                                                            .size ||
                                                                        200,
                                                                }),
                                                        }}
                                                    >
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </Td>
                                                ))}
                                        </Tr>
                                    ))
                            )}
                        </TBody>
                    )}
                </Table>
            </div>
            {isPageSelector && (
                <div className="flex items-center justify-between mt-4">
                    <Pagination
                        pageSize={pageSize}
                        currentPage={pageIndex}
                        total={total}
                        onChange={handlePaginationChange}
                    />
                    <div style={{ minWidth: 130 }}>
                        <Select
                            size="sm"
                            menuPlacement="top"
                            isSearchable={false}
                            value={pageSizeOption.filter(
                                (option) => option.value === pageSize
                            )}
                            options={pageSizeOption}
                            onChange={(option) =>
                                handleSelectChange(option.value)
                            }
                        />
                    </div>
                </div>
            )}
        </Loading>
    )
})

DataTable.propTypes = {
    columns: PropTypes.array,
    data: PropTypes.array,
    loading: PropTypes.bool,
    onCheckBoxChange: PropTypes.func,
    onIndeterminateCheckBoxChange: PropTypes.func,
    onPaginationChange: PropTypes.func,
    onSelectChange: PropTypes.func,
    onSort: PropTypes.func,
    pageSizes: PropTypes.arrayOf(PropTypes.number),
    selectable: PropTypes.bool,
    skeletonAvatarColumns: PropTypes.arrayOf(PropTypes.number),
    skeletonAvatarProps: PropTypes.object,
    pagingData: PropTypes.shape({
        total: PropTypes.number,
        pageIndex: PropTypes.number,
        pageSize: PropTypes.number,
    }),
}

DataTable.defaultProps = {
    pageSizes: [10, 25, 50, 100],
    pagingData: {
        total: 0,
        pageIndex: 1,
        pageSize: 10,
    },
    data: [],
    columns: [],
    selectable: false,
    loading: false,
}

export default DataTable
